import { Artefact, ArtefactType, ArtefactDetectionConfig } from "@/types/artefacts";

import { BaseArtefactDetector } from "./BaseDetector";

/**
 * Detects HTML content from code blocks
 */
export class HTMLDetector extends BaseArtefactDetector {
  detect(content: string, messageId: string, _config: ArtefactDetectionConfig) {
    if (!this.validateContent(content)) {
      return this.createResult([], 0, ["Invalid content provided for HTML detection"]);
    }

    const artefacts: Artefact[] = [];
    const warnings: string[] = [];
    let skippedCount = 0;
    let htmlIndex = 0;
    const detectedPositions: Array<{ start: number; end: number }> = [];

    // Detect HTML blocks (```html code blocks)
    const htmlRegex = /```html\s*\n([\s\S]*?)```/g;
    let match;
    let foundHtmlBlocks = false;

    while ((match = htmlRegex.exec(content)) !== null) {
      foundHtmlBlocks = true;
      const htmlContent = match[1].trim();

      // Skip small HTML fragments (increased threshold for better filtering)
      if (htmlContent.length > 200) {
        // Regular HTML content - extract meaningful title
        const title = this.generateHtmlTitle("HTML Content", htmlContent);

        const artefact: Artefact = {
          id: `${messageId}-html-${htmlIndex}`,
          type: ArtefactType.HTML,
          title,
          content: htmlContent,
          messageId,
          timestamp: this.getCurrentTimestamp(),
          metadata: {
            fileExtension: "html",
            detectionPattern: "html_block",
            estimatedSize: `${htmlContent.length} chars`,
          },
        };

        artefacts.push(artefact);
        detectedPositions.push({ start: match.index, end: match.index + match[0].length });

        htmlIndex++;
      } else {
        skippedCount++;
      }
    }

    // Detect raw HTML documents (not wrapped in code blocks)
    // Pattern 1: Full HTML documents with DOCTYPE
    const rawHtmlWithDoctypeRegex = /<!DOCTYPE\s+html>[\s\S]*?<html[\s\S]*?<\/html>/gi;
    let rawMatch: RegExpExecArray | null;

    while ((rawMatch = rawHtmlWithDoctypeRegex.exec(content)) !== null) {
      const htmlContent = rawMatch[0].trim();

      // Check if this HTML was already detected as part of a code block
      const isOverlapping = detectedPositions.some(
        (pos) => rawMatch!.index >= pos.start && rawMatch!.index < pos.end,
      );

      if (!isOverlapping && htmlContent.length > 200) {
        const title = this.generateHtmlTitle("HTML Content", htmlContent);

        const artefact: Artefact = {
          id: `${messageId}-html-${htmlIndex}`,
          type: ArtefactType.HTML,
          title,
          content: htmlContent,
          messageId,
          timestamp: this.getCurrentTimestamp(),
          metadata: {
            fileExtension: "html",
            detectionPattern: "html_document",
            estimatedSize: `${htmlContent.length} chars`,
          },
        };

        artefacts.push(artefact);
        detectedPositions.push({
          start: rawMatch!.index,
          end: rawMatch!.index + rawMatch[0].length,
        });

        htmlIndex++;
      }
    }

    // Pattern 2: HTML documents without DOCTYPE (just <html> tag)
    const rawHtmlWithoutDoctypeRegex = /<html[\s\S]*?<\/html>/gi;
    let rawMatchNoDt: RegExpExecArray | null;

    while ((rawMatchNoDt = rawHtmlWithoutDoctypeRegex.exec(content)) !== null) {
      const htmlContent = rawMatchNoDt[0].trim();

      // Check if this HTML was already detected
      const isOverlapping = detectedPositions.some(
        (pos) => rawMatchNoDt!.index >= pos.start && rawMatchNoDt!.index < pos.end,
      );

      if (!isOverlapping && htmlContent.length > 200) {
        const title = this.generateHtmlTitle("HTML Content", htmlContent);

        const artefact: Artefact = {
          id: `${messageId}-html-${htmlIndex}`,
          type: ArtefactType.HTML,
          title,
          content: htmlContent,
          messageId,
          timestamp: this.getCurrentTimestamp(),
          metadata: {
            fileExtension: "html",
            detectionPattern: "html_document",
            estimatedSize: `${htmlContent.length} chars`,
          },
        };

        artefacts.push(artefact);

        htmlIndex++;
      }
    }

    // Only return skip count if we actually found HTML blocks to process
    return this.createResult(artefacts, foundHtmlBlocks ? skippedCount : 0, warnings);
  }

  /**
   * Generate a descriptive title for HTML content
   */
  private generateHtmlTitle(_htmlType: string, content: string): string {
    // Try to extract title tag
    const titleMatch = content.match(/<title[^>]*>([^<]+)<\/title>/i);

    if (titleMatch) {
      let fullTitle = titleMatch[1].trim();

      // Clean up any markdown code block syntax from the title
      fullTitle = fullTitle.replace(/```\w*\s*/g, "");

      const truncatedTitle = fullTitle.substring(0, 50);

      return `${truncatedTitle}${fullTitle.length > 50 ? "..." : ""}`;
    }

    // Try to extract first heading
    const h1Match = content.match(/<h1[^>]*>([^<]+)<\/h1>/i);

    if (h1Match) {
      let fullHeading = h1Match[1].trim();

      // Clean up any markdown code block syntax from the heading
      fullHeading = fullHeading.replace(/```\w*\s*/g, "");

      const truncatedHeading = fullHeading.substring(0, 40);

      return `${truncatedHeading}${fullHeading.length > 40 ? "..." : ""}`;
    }

    // Look for any other heading
    const headingMatch = content.match(/<h[2-6][^>]*>([^<]+)<\/h[2-6]>/i);

    if (headingMatch) {
      let fullHeading = headingMatch[1].trim();

      // Clean up any markdown code block syntax from the heading
      fullHeading = fullHeading.replace(/```\w*\s*/g, "");

      const truncatedHeading = fullHeading.substring(0, 40);

      return `${truncatedHeading}${fullHeading.length > 40 ? "..." : ""}`;
    }

    // Check if it's a chart or visualization
    const lowerContent = content.toLowerCase();

    if (lowerContent.includes("chart") || lowerContent.includes("graph")) {
      const chartTuples: [string, string][] = [
        ["dot", "Dot Plot Chart"],
        ["column", "Column Chart"],
        ["grouped", "Grouped Chart"],
        ["stacked", "Stacked Chart"],
        ["spider", "Spider Chart"],
        ["radar", "Radar Chart"],
        ["donut", "Donut Chart"],
        ["step", "Step Chart"],
        ["area", "Area Chart"],
        ["pie", "Pie Chart"],
        ["bar", "Bar Chart"],
        ["scatter", "Scatter Plot"],
        ["spline", "Spline Chart"],
        ["line", "Line Chart"],
      ];

      for (const [keyword, title] of chartTuples) {
        if (lowerContent.includes(keyword)) return title;
      }

      return "Chart Visualization";
    }

    // Fallback
    return "HTML Content";
  }
}
